import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellSimpleRinging, X } from "@phosphor-icons/react";
import { isPushSupported, currentPermission, enablePush } from "@/lib/push";
import { useAuth } from "@/context/AuthContext";

const DISMISSED_KEY = "bkpro_push_dismissed_at";
const REPROMPT_DAYS = 7;

/**
 * Discrete banner shown at the top of the app when:
 *  - user is logged in
 *  - browser supports Web Push
 *  - permission is 'default' (not yet asked, not denied)
 *  - user hasn't dismissed the banner within the last 7 days
 * Requesting permission MUST come from a user click — that's what the button does.
 */
export default function PushPermissionPrompt() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!isPushSupported()) return;
    if (currentPermission() !== "default") return;
    // Respect dismissal window
    try {
      const raw = localStorage.getItem(DISMISSED_KEY);
      if (raw) {
        const ts = new Date(raw).getTime();
        if (Date.now() - ts < REPROMPT_DAYS * 86400_000) return;
      }
    } catch (e) {
      // localStorage disabled (private browsing) — safe to just show the banner
      console.debug("[push-prompt] localStorage read blocked:", e?.message || e);
    }
    setVisible(true);
  }, [user]);

  const dismiss = () => {
    // Stored value is only a dismiss timestamp — no PII/tokens.
    try { localStorage.setItem(DISMISSED_KEY, new Date().toISOString()); } catch (e) { console.debug("[push-prompt] localStorage write blocked:", e?.message || e); }
    setVisible(false);
  };

  const onEnable = async () => {
    setBusy(true);
    const r = await enablePush();
    setBusy(false);
    if (r.ok) {
      toast.success("Notifications activées");
      setVisible(false);
    } else if (r.reason === "denied") {
      toast.error("Autorisation refusée — activez-les dans les réglages du navigateur");
      dismiss();
    } else if (r.reason === "unsupported") {
      dismiss();
    } else {
      toast.error("Impossible d'activer — réessayez plus tard");
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed top-2 left-2 right-2 z-40 rounded-2xl border border-[#005CFF]/30 bg-[#0F1930]/95 backdrop-blur-lg p-3 flex items-center gap-3 shadow-lg animate-in fade-in slide-in-from-top-2"
      data-testid="push-prompt-banner"
    >
      <div className="w-9 h-9 rounded-xl bg-[#005CFF]/20 flex items-center justify-center shrink-0">
        <BellSimpleRinging size={18} weight="duotone" color="#7CB2FF" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">Activer les notifications</div>
        <div className="text-[11px] text-white/60 leading-tight">Sois averti d&apos;une nouvelle intervention même app fermée.</div>
      </div>
      <button
        onClick={onEnable}
        disabled={busy}
        className="text-xs font-semibold bg-[#005CFF] text-white px-3 py-1.5 rounded-full active:scale-95 disabled:opacity-50 shrink-0"
        data-testid="push-enable-btn"
      >
        {busy ? "…" : "Activer"}
      </button>
      <button
        onClick={dismiss}
        className="w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-white/70 active:scale-95 shrink-0"
        aria-label="Fermer"
        data-testid="push-dismiss-btn"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
